import { ProductFlowDataCollection } from '../product-flow/product-flow.types';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { RootReducer, Store } from '@app/app.reducers';
import { DetailsData } from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import { setStepStateValid } from '@app/product/reducers/product-flow.reducer';
import { SelectedOptionsEvent } from '@app/product/reducers/product.reducer';
import { ProductEditorService } from '@app/product/services/product-editor.service';
import {
	generateFileBucketUrl,
	hasSelectableOptions,
	isFormProduct,
	isVariableProduct,
} from '@app/product/utils/product-utils';
import { ViewportService } from '@app/shared/services/viewport.service';
import { I18nService } from '@app/shared/utils/i18n.service';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload';
import { Observable, of as observableOf } from 'rxjs';

@Component({
	selector: 'g-details',
	templateUrl: './details.component.html',
	styleUrls: ['./details.component.scss'],
})
export class DetailsComponent implements OnInit, ProductFlowDataCollection {
	@Input() readonly product: api.ProductDto;
	@Input() readonly category: api.Category;
	@Input() readonly showPrices = true;
	@Input() readonly showInternalInfo = false;
	@Output() readonly selectedOptionsChanged = new EventEmitter<SelectedOptionsEvent>();
	mediaItems$: Observable<api.ProductImageDto[]>;
	selectedOptions: SelectedOptionsEvent;
	hasSelectableOptions: boolean;
	printfileDownloadUrls: string[];
	isInStock = true;
	isVariableProduct = false;
	isFormProduct = false;
	isDescriptionExpanded = false;
	isTechnicalDescriptionExpanded = false;
	isInternalInfoExpanded = false;
	icons = { faDownload, faAngleRight };

	constructor(
		public productEditor: ProductEditorService,
		private readonly store: Store<RootReducer.State>,
		private readonly i18n: I18nService,
		public readonly viewport: ViewportService,
	) {}

	ngOnInit(): void {
		this.setProductInformation();
		this.setPrintfileDownloadUrls();
		// Always set step state valid, validity is checked by product editor selection state
		this.store.dispatch(setStepStateValid(true));
	}

	collectStepData(): DetailsData {
		return {
			workTitle: this.productEditor.workTitle || this.i18n.getText(this.product.locale.product_name),
		};
	}

	optionsChanged($event: SelectedOptionsEvent): void {
		this.selectedOptions = $event;
		this.selectedOptionsChanged.emit($event);
	}

	showProductPrice(): boolean {
		const hasErrors = !!Object.entries(this.selectedOptions?.errors || {}).some(
			([key, error]) =>
				// Hide price if fields with errors affect the calculations
				error && key === 'quantity',
		);
		return this.showPrices && !this.isFormProduct && !hasErrors;
	}

	private setProductInformation() {
		this.hasSelectableOptions = hasSelectableOptions(this.product);
		this.isInStock = Number.isFinite(this.product.stock_level) && this.product.stock_level > 0;
		this.isVariableProduct = isVariableProduct(this.product);
		this.isFormProduct = isFormProduct(this.product);
		this.mediaItems$ = observableOf([this.product.images.master, ...this.product.images.additional]);
	}

	private setPrintfileDownloadUrls() {
		const isDownloadAllowed = this.product?.metadata?.allow_print_file_download === true;
		if (!isDownloadAllowed) {
			this.printfileDownloadUrls = [];
			return;
		}

		// TODO: See if properties.printfile can be removed from here.
		// The check is kept here still checked here to prevent any unintended behavior.
		const printfiles = [
			this.product?.properties?.printfile,
			...(this.product?.properties?.printfiles ?? []),
		] as api.PrintfileDto[];

		this.printfileDownloadUrls = printfiles
			.map((printfile) => {
				if (printfile?.key?.length > 0) return generateFileBucketUrl(printfile.key);
			})
			.filter(Boolean);
	}
}
